import { Button, Icon, Squircle, Typography, XStack, YStack } from '@shinetools/lumen-react'
import { colors } from '@shinetools/lumen-tokens/web/colors'
import { ChevronRight, TriangleAlert } from 'lucide-react'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { Scope } from '../../../../../../enums/scope'
import { isAuthorized } from '../../../../../../utils/isAuthorized'
import { widgetId } from '../../../../constants/widgetId'
import { useWidgetsActions } from '../../../../hooks/useWidgetsActions'
import { BankIntegrationActionPoint } from '../../enums/bankIntegrationActionPoint'

interface BankConnectionNeedsAttentionProps {
  count: number
  accountsUrl?: string
}

export const BankConnectionNeedsAttention = ({
  count,
  accountsUrl,
}: BankConnectionNeedsAttentionProps): ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { trackDashboardActionPerformed } = useWidgetsActions()

  const handleClick = useCallback(() => {
    trackDashboardActionPerformed({
      action: BankIntegrationActionPoint.RenewConnection,
      widgetId: widgetId.BankIntegration,
    })

    if (accountsUrl) {
      navigate(accountsUrl)
    }
  }, [trackDashboardActionPerformed, navigate, accountsUrl])

  return (
    <YStack gap={16} flex={1}>
      <XStack gap={16} align="center">
        <Squircle
          size="large"
          color="orange"
          variant="subtle"
          icon={<Icon icon={TriangleAlert} color={colors.content.warning.bold} />}
        />
        <Typography.Header as="h2">{count}</Typography.Header>
        <Typography.Text size="large" weight="medium">
          {t('dashboard.widget.bank-integration.cpa.disconnected.title', { count })}
        </Typography.Text>
      </XStack>
      <Typography.Text color={colors.content.neutral.medium}>
        {t('dashboard.widget.bank-integration.cpa.disconnected.info_text')}
      </Typography.Text>
      {isAuthorized(Scope.BankSyncWrite) && (
        <XStack justify="flex-end">
          <Button
            label={t('dashboard.widget.bank-integration.cpa.disconnected.cta', { count })}
            endIcon={<Icon icon={ChevronRight} />}
            onClick={handleClick}
          />
        </XStack>
      )}
    </YStack>
  )
}
